import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Topbar from "../../components/topbar/Topbar";
import "./applicationSelector.css";
import {
  APPLICATION_DB_EDITOR,
  APPLICATION_PARTS_CATALOG,
  getAccessibleApplications,
  getApplicationLabel,
  getApplicationRoute,
  hasAdminRole,
  parseStoredAppRoleIds,
  parseStoredRoleIds,
} from "../../utils/authAccess";

export default function ApplicationSelector() {
  const navigate = useNavigate();
  const [unitNumber, setUnitNumber] = useState(localStorage.getItem("unitNumber") || "");
  const [selectedApplication, setSelectedApplication] = useState("");
  const [error, setError] = useState("");

  const appRoleIds = useMemo(
    () => parseStoredAppRoleIds(localStorage.getItem("appRoleIds")),
    []
  );
  const roleIds = useMemo(
    () => parseStoredRoleIds(localStorage.getItem("roleId")),
    []
  );
  const accessibleApplications = useMemo(
    () => getAccessibleApplications(appRoleIds),
    [appRoleIds]
  );
  const isAdmin = useMemo(() => hasAdminRole(roleIds), [roleIds]);

  useEffect(() => {
    if (!accessibleApplications.length) {
      navigate("/login");
      return;
    }

    if (accessibleApplications.length === 1) {
      const onlyApplication = accessibleApplications[0];

      if (onlyApplication === APPLICATION_PARTS_CATALOG) {
        localStorage.setItem("application", onlyApplication);
        localStorage.setItem("unitNumber", "");
        localStorage.setItem("pendingUnitNumber", "");
        navigate(getApplicationRoute(onlyApplication));
        return;
      }

      const existingUnit = (localStorage.getItem("unitNumber") || "").trim();
      if (existingUnit.length === 4) {
        localStorage.setItem("application", onlyApplication);
        localStorage.setItem("unitNumber", existingUnit);
        localStorage.setItem("pendingUnitNumber", "");
        navigate(getApplicationRoute(onlyApplication));
      } else {
        setSelectedApplication(onlyApplication);
      }
      return;
    }

    if (!selectedApplication) {
      setSelectedApplication(accessibleApplications[0]);
    }
  }, [accessibleApplications, navigate, selectedApplication]);

  const handleSelectApplication = (applicationKey) => {
    setSelectedApplication(applicationKey);
    setError("");
  };

  const handleContinue = () => {
    if (!selectedApplication) {
      setError("Please select an application.");
      return;
    }

    const applicationKey = selectedApplication;
    if (applicationKey === APPLICATION_DB_EDITOR) {
      const unit = unitNumber.trim();
      if (!unit || unit.length !== 4) {
        setError("Unit number is required and must be exactly 4 characters for DB Editor.");
        return;
      }
      localStorage.setItem("unitNumber", unit);
    } else {
      localStorage.setItem("unitNumber", "");
    }

    localStorage.setItem("application", applicationKey);
    localStorage.setItem("pendingUnitNumber", "");
    navigate(getApplicationRoute(applicationKey));
  };

  const handleOpenAdmin = () => {
    navigate("/admin-access");
  };

  return (
    <div className="mainPageContent">
      <Topbar title="DB EDITOR" showAppSwitch={false} />
      <div className="applicationSelectorPage">
        <div className="applicationSelectorCard">
          <h2>Select the Application</h2>

          <div className="applicationSelectorOptions">
            {accessibleApplications.map((applicationKey) => (
              <button
                key={applicationKey}
                type="button"
                className={`applicationOptionCard ${
                  selectedApplication === applicationKey ? "active" : ""
                }`}
                onClick={() => handleSelectApplication(applicationKey)}
              >
                <span>{getApplicationLabel(applicationKey)}</span>
              </button>
            ))}
          </div>

          {selectedApplication === APPLICATION_DB_EDITOR ? (
            <label className="applicationSelectorLabel">
              Unit Number
              <input
                type="text"
                maxLength={4}
                value={unitNumber}
                onChange={(event) => {
                  setUnitNumber(event.target.value);
                  if (error) {
                    setError("");
                  }
                }}
                placeholder="Enter 4-digit unit number"
              />
            </label>
          ) : null}

          <div className="applicationSelectorActions">
            <button className="applicationActionButton" onClick={handleContinue}>
              Continue
            </button>

            {isAdmin ? (
              <button
                className="applicationActionButton adminActionButton"
                onClick={handleOpenAdmin}
              >
                Open Admin Controls
              </button>
            ) : null}
          </div>

          {error ? <p className="applicationSelectorError">{error}</p> : null}
        </div>
      </div>
    </div>
  );
}
